import {TError, TInventoryMedicineUpdate} from '@app/types';
import showNotification from '@app/utils/showNotification';
import {Form} from 'antd';
import {t} from 'i18next';
import {useEffect} from 'react';
import {LogEvents, useSendEvent} from '@app/utils/logger';
import {
  useGetInventoryMedicineQuery,
  useUpdateInventoryMedicineMutation,
} from '@app/redux/services/inventoryService';

interface IUseInventoryForm {
  medicineId: string;
  date: string;
  onCancel: () => void;
}

export const useInventoryForm = ({
  date,
  medicineId,
  onCancel,
}: IUseInventoryForm) => {
  const [form] = Form.useForm<TInventoryMedicineUpdate>();
  const sendEvent = useSendEvent();
  const {isLoading: isFetching, data: inventory} = useGetInventoryMedicineQuery(
    {date, id: medicineId},
  );
  const [updateInventory, {isLoading: isUpdating}] =
    useUpdateInventoryMedicineMutation();

  useEffect(() => {
    if (!inventory) return;
    form.setFieldsValue({
      box_count: inventory.box_count,
      package_count: inventory.package_count,
      piece_count: inventory.piece_count,
      expiration_date: inventory.expiration_date,
    });
  }, [inventory, form]);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      sendEvent(LogEvents.INVENTORY_DETAIL_EDIT_PRESS, {
        inventoryId: inventory?.id,
      });
      await updateInventory({
        date,
        id: medicineId,
        body: values,
      }).unwrap();
      const successMessage = inventory?.id
        ? t('Successfully updated!')
        : t('Successfully registered!');
      showNotification(t('Success'), successMessage, 'bottomRight', 'success');
      onCancel();
      form.resetFields();
    } catch (error) {
      const {data: errorData} = error as TError;
      if (inventory?.id) {
        sendEvent(LogEvents.ERROR_PHARMACY_EDIT, {
          inventoryId: inventory?.id,
          ...errorData,
        });
      } else {
        sendEvent(LogEvents.INVENTORY_REGISTER_ERROR, errorData);
      }
    }
  };

  return {
    form,
    isLoading: isFetching || isUpdating,
    onSubmit,
  };
};
